/**
 * ShapeSkillEffect — Fire tree Shape Fork: a tri-modal incantation tool.
 *
 * Three sub-modes selected at activation time by `deps.getActivationMode()`:
 *
 * **wall** — Place a 4-tile linear fire wall across a corridor. Creates a
 *   `fire_wall` zone with `shape: 'line'`. Duration: 8000ms base, augmented by
 *   `wallDurationMs` (Fixed Point: +6000ms). Projectile blocking disabled until
 *   Fixed Point's `wallBlocksProjectiles` key is set.
 *
 * **bolt** — Fire a shaped bolt at medium range. Calls `projectiles.fire` with
 *   `statusToApply: 'burning'`, `rangeTiles: 4`. Sculpt's bolt-split behavior
 *   is handled after impact (external to this class).
 *
 * **shove** — Move an existing fire zone 1 tile in a direction. Calls
 *   `deps.zones.moveZone(nearestZoneId, dx, dy, 1)`.
 *
 * Long cooldown tier. Breaks Hide on activation. Fizzle chance elevated at unlock.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see fire-spec.md § Shape, § Sculpt, § Fixed Point, § Living Architecture
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ShapeWallZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly lengthTiles: number;
  readonly widthTiles: number;
  readonly facingAngle: number;
  readonly durationMs: number;
  readonly dotDefId: string;
  readonly dotIntensity: number;
  readonly tickIntervalMs: number;
  readonly sourceSkillId: string;
  readonly blocksProjectiles: boolean;
  /** Entity immune to this zone's DoT (the player). */
  readonly friendlyEntityId?: string;
  /**
   * Living Architecture: called when an entity enters or passes through the
   * wall zone. Applies a burning DoT to the entity.
   */
  readonly onEntityEnter?: (entityId: string) => void;
}

export interface ShapeBoltConfig {
  readonly type: string;
  readonly damage: number;
  readonly rangeTiles: number;
  readonly statusToApply: string;
  readonly sourceSkillId: string;
  /** Sculpt: number of sub-bolts to spawn on impact (0 or absent = no split). */
  readonly splitCount?: number;
  /** Sculpt: damage multiplier for each split bolt (e.g. 0.6 = 60%). */
  readonly splitDamageMultiplier?: number;
}

export interface ShapeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(treeId: string, key: string, aggregation: 'product' | 'sum'): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /**
   * Living Architecture: apply a burning DoT to a specific entity.
   * Called by the wall's onEntityEnter hook when shape_passive is unlocked.
   */
  readonly applyContactBurn?: (entityId: string, intensity: number, durationMs: number, sourceSkillId: string) => void;
  /**
   * Returns the mode to use for this activation.
   * 'wall' = place fire wall, 'bolt' = fire bolt, 'shove' = move nearest zone.
   */
  readonly getActivationMode: () => 'wall' | 'bolt' | 'shove';
  /** Player's current aim direction angle in radians (for wall orientation). */
  readonly getPlayerAimAngle: () => number;
  /** Player's current world position (for wall placement). */
  readonly getPlayerPosition: () => { x: number; y: number };
  /** Player's entity ID for friendly-fire exclusion on walls. */
  readonly getPlayerEntityId: () => string;
  /**
   * Returns the ID of the nearest existing fire zone (for shove mode).
   * Returns null if none.
   */
  readonly getNearestFireZoneId: () => string | null;
  /**
   * Returns the shove direction (for shove mode).
   * `{ dx, dy }` normalized (-1, 0, or 1).
   */
  readonly getShoveDirection: () => { dx: number; dy: number } | null;
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide: () => void;
  readonly zones: {
    create(config: ShapeWallZoneConfig): string;
    moveZone(zoneId: string, dx: number, dy: number, tiles: number): void;
  };
  readonly projectiles: {
    fire(config: ShapeBoltConfig): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ShapeConfig {
  readonly cooldownMs: number;
  /** Base wall duration before Fixed Point addition. */
  readonly baseWallDurationMs: number;
  /** Wall linear length in tiles. */
  readonly wallLengthTiles: number;
  /** Wall width in tiles (entity-detection footprint). */
  readonly wallWidthTiles: number;
  /** Wall DoT intensity per tick. */
  readonly wallDotIntensity: number;
  /** Wall DoT tick interval in ms. */
  readonly wallTickIntervalMs: number;
  /** Bolt base damage. */
  readonly boltDamage: number;
  /** Bolt range in tiles. */
  readonly boltRangeTiles: number;
}

/** Snap an angle in radians to the nearest 45° (π/4) increment. */
function snapTo45Degrees(angleRad: number): number {
  const step = Math.PI / 4;
  return Math.round(angleRad / step) * step;
}

const DEFAULT_CONFIG: ShapeConfig = {
  cooldownMs: 2000,           // long cooldown tier
  baseWallDurationMs: 8000,   // 8s base; Fixed Point adds +6000ms
  wallLengthTiles: 4,
  wallWidthTiles: 0.5,
  wallDotIntensity: 2,
  wallTickIntervalMs: 1000,
  boltDamage: 12,             // placeholder — final value in combat config
  boltRangeTiles: 4,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ShapeSkillEffect implements SkillEffect {
  readonly skillId = 'shape';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: ShapeDeps;
  private readonly config: ShapeConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _lastMode: 'wall' | 'bolt' | 'shove' | null = null;

  constructor(deps: ShapeDeps, config: Partial<ShapeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get lastMode(): 'wall' | 'bolt' | 'shove' | null { return this._lastMode; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    if (!this.deps.rollSkillSuccess('shape')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    this.deps.breakHide();

    const mode = this.deps.getActivationMode();
    this._lastMode = mode;

    switch (mode) {
      case 'wall':
        this._activateWall();
        break;
      case 'bolt':
        this._activateBolt();
        break;
      case 'shove':
        this._activateShove();
        break;
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  private _activateWall(): void {
    // Fixed Point: flat duration addition
    const bonusDuration = this.deps.resolver.getAdditiveEffect('shape', 'wallDurationMs');
    const blocksProjectiles = this.deps.resolver.hasEffect('shape', 'wallBlocksProjectiles');

    // Architect: duration multiplier (mutually exclusive with Fixed Point in practice,
    // but the resolver query is harmless — it returns 1.0 if Architect isn't loaded).
    const durationMultiplier = this.deps.resolver.getTreePassiveEffect('fire', 'durationMultiplier', 'product');
    const resolvedDuration = Math.round(
      (this.config.baseWallDurationMs + bonusDuration) * durationMultiplier,
    );

    // Living Architecture (shape_passive): enemies that enter or stand adjacent
    // to the wall catch a burning DoT. The detection zone is extended to 1 tile
    // from the wall center (2 tiles total width) to catch adjacent entities.
    const contactDotDamage = this.deps.resolver.getTreePassiveEffect('fire', 'wallContactDotDamage', 'sum');
    const contactDotDurationMs = this.deps.resolver.getTreePassiveEffect('fire', 'wallContactDotDurationMs', 'sum');
    const hasContactBurn = contactDotDamage > 0 && this.deps.applyContactBurn !== undefined;

    // When Living Architecture is active, extend the detection width so entities
    // within 1 tile of the wall center are caught (half-width = 1 tile = 2 tiles total).
    const detectionWidthTiles = hasContactBurn ? 2.0 : this.config.wallWidthTiles;

    const onEntityEnter = hasContactBurn
      ? (entityId: string) => {
          this.deps.applyContactBurn!(entityId, contactDotDamage, contactDotDurationMs, 'shape_passive');
        }
      : undefined;

    const position = this.deps.getPlayerPosition();
    // Sculpt: walls snap to 45° increments; without Sculpt, free-aim is preserved.
    const hasSculpt = this.deps.resolver.hasEffect('shape', 'boltSplitCount');
    const rawAimAngle = this.deps.getPlayerAimAngle();
    const facingAngle = hasSculpt ? snapTo45Degrees(rawAimAngle) : rawAimAngle;
    const playerEntityId = this.deps.getPlayerEntityId();

    this.deps.zones.create({
      id: `shape-wall-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'fire_wall',
      position,
      shape: 'line',
      lengthTiles: this.config.wallLengthTiles,
      widthTiles: detectionWidthTiles,
      facingAngle,
      durationMs: resolvedDuration,
      dotDefId: 'burning',
      dotIntensity: this.config.wallDotIntensity,
      tickIntervalMs: this.config.wallTickIntervalMs,
      sourceSkillId: 'shape',
      blocksProjectiles,
      friendlyEntityId: playerEntityId,
      ...(onEntityEnter !== undefined && { onEntityEnter }),
    });
  }

  private _activateBolt(): void {
    // Sculpt: pass split info to the projectile system so it spawns sub-bolts on impact.
    const splitCount = this.deps.resolver.getAdditiveEffect('shape', 'boltSplitCount');
    const splitDamageMultiplier = this.deps.resolver.getAdditiveEffect('shape', 'boltSplitDamageMultiplier');

    this.deps.projectiles.fire({
      type: 'bolt',
      damage: this.config.boltDamage,
      rangeTiles: this.config.boltRangeTiles,
      statusToApply: 'burning',
      sourceSkillId: 'shape',
      ...(splitCount > 0 ? { splitCount, splitDamageMultiplier } : {}),
    });
  }

  private _activateShove(): void {
    const zoneId = this.deps.getNearestFireZoneId();
    if (!zoneId) return;
    const dir = this.deps.getShoveDirection();
    if (!dir) return;
    this.deps.zones.moveZone(zoneId, dir.dx, dir.dy, 1);
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
